import React from 'react';
import PropTypes from 'prop-types';

import * as Sentry from '@sentry/browser';

import { useTranslation } from 'react-i18next';
import { useSegment } from '../../../../../components/tracking/trackingProvider';
import { Card, HeaderCard, TitleCard, SubTitleCard, Icon, Button, ToastContext } from '@weezevent/nacre';

import Style from './filledForm.module.css';
import LoadingCard from '../../../../../components/loadingCard';
import useAppContext from '../../../../../context/useAppContext';
import FormAnswers from './form-answers/formAnswers';
import FieldModel from '../../../../../models/form_field';
import { FormPreview } from '../../../../forms/components/formPreview';
import { useForm } from '../../../../../utils/validators';
import FormEntry from '../../../../../models/form_entry';
import Auth from '../../../../../utils/auth';
import { useHistory } from 'react-router-dom';

//ws-36 - field 'confirmMail'
const fieldsNotDisplayable = ['ws-36', 'ws-43'];
const customfieldTypesNotDisplayable = ['address_autocomplete', 'latitude_longitude'];
const systemFieldsNotEditable = ['wc-barcode_serial'];
const FIELD_EMAIL_BUYER = 'ws-5';
const FIELD_EMAIL_ATTENDEE = 'ws-5';

function FilledFormHeader({ title = null, subtitle = null, onEdit = () => {}, displayEdit = false }) {
    const { t } = useTranslation();
    return (
        <div className={Style['header']}>
            <div className={Style['title-container']}>
                {title && <TitleCard title={title} />}
                {subtitle && <SubTitleCard title={subtitle} />}
            </div>
            {displayEdit && (
                <Auth allow={['RoleCapacity.ADMIN', 'PyvarCapacity.ADMIN', 'PyvarCapacity.ATTENDEE', 'PyvarCapacity.ORDER']}>
                    <div className={Style['edit']} onClick={onEdit}>
                        <span className={Style['edit-label']}>{t('ticket.common.action.edit')}</span>
                        <Icon name="edit" className={Style['edit-icon']} />
                    </div>
                </Auth>
            )}
        </div>
    );
}

function FilledFormCard({
    className = '',
    title = null,
    subtitle = null,
    attendee = null,
    order = null,
    locale = 'en-GB',
    buyerForm = false,
    initialForm = {},
    hiddenForm = {},
    answers = {},
    formEntries = [],
    loading = true,
    onSubmit = () => {},
    onEdit = () => {},
    usage = 'attendee',
    ...rest
}) {
    const { t } = useTranslation();
    const { track } = useSegment();
    const toast = React.useContext(ToastContext);

    const history = useHistory();
    const [{ event, organization }] = useAppContext();

    const [edit, setEdit] = React.useState(false);
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    const [form, setForm, setFormObject] = useForm({ ...initialForm }, {});

    //Filter fields to display only editable fields
    const formEntriesEditable = React.useMemo(() => {
        return formEntries.filter(f => !systemFieldsNotEditable.includes(f.form_field.weez_id));
    }, [formEntries]);

    const handleSubmit = React.useCallback(() => {
        setIsSubmitting(true);

        //Remove form_field type === 'divider'
        let _form = { ...hiddenForm, ...form };
        Object.keys(_form).forEach(key => {
            if (_form[key] === 'form_field_divider') {
                delete _form[key];
            }
        });

        track('ACTION > SAVE', { 'Page Title': usage === 'attendee' ? 'ATTENDEE PROFILE' : 'ORDER DETAILS' });
        if (usage === 'buyer') {
            let _order = order.copy();

            // To prevent synchronization from raising errors, here we ensure that email fields are either
            // set with proper email adresses, or null.
            if (_form['ws-5'] === '') {
                _form['ws-5'] = null;
            }

            _order.buyer_form = _form;

            // If the order has an email and the form email is changed, the order email should also be updated.
            if (_order.buyer_email !== _form[FIELD_EMAIL_BUYER]) {
                _order.buyer_email = _form[FIELD_EMAIL_BUYER];
            }

            return _order
                .save({
                    organizationId: organization.id,
                    eventId: event.id
                })
                .then(() => {
                    toast.success(t(`ticket.manager.orders.detail.buyerEditSuccess`));
                    onSubmit();
                    setEdit(false);
                })
                .catch(e => {
                    console.error(e);
                    Sentry.captureException(e);
                    toast.error(t(`ticket.manager.orders.detail.buyerEditError`));
                })
                .finally(() => {
                    setIsSubmitting(false);
                });
        }

        //copy the participant to keep the original in case of error if the form submission fails
        let _attendee = attendee.copy();

        _attendee.form = _form;
        _attendee.rate = attendee.rate.id;

        // If the attendee has an email and the form email is changed, the attendee email should also be updated.
        if (attendee.email !== _attendee.form[FIELD_EMAIL_ATTENDEE]) {
            _attendee.email = _attendee.form[FIELD_EMAIL_ATTENDEE];
        }

        _attendee
            .save({
                organizationId: organization.id,
                eventId: event.id
            })
            .then(() => {
                toast.success(t(`ticket.manager.attendees.detail.filledForm.editSuccess`));
                onSubmit();
                setEdit(false);
            })
            .catch(e => {
                console.error(e);
                Sentry.captureException(e);
                toast.error(t(`ticket.manager.attendees.detail.filledForm.editError`));
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    }, [attendee, form, hiddenForm, organization, event, onSubmit, order, usage]);

    return (
        <>
            <Card {...rest}>
                <HeaderCard>
                    <FilledFormHeader
                        title={title}
                        subtitle={subtitle}
                        onEdit={() => {
                            setEdit(true);
                            onEdit();
                        }}
                        displayEdit={Boolean(!loading && formEntries.length)}
                    />
                </HeaderCard>
                {loading ? (
                    <Card.Content>
                        <LoadingCard loading />
                    </Card.Content>
                ) : (
                    <Card.Content>
                        {Boolean(!Object.keys(answers).length && !formEntries.length) && (
                            <Card.EmptyState
                                icon={<Icon name="list" />}
                                title={t('ticket.common.noData')}
                                subtitle={!attendee.rate.form ? t('ticket.manager.attendees.detail.filledForm.subtitleEmptyState') : null}
                                labelButton={!attendee.rate.form ? t('ticket.manager.attendees.detail.filledForm.createForm') : null}
                                onClick={() => {
                                    track('ACTION > CREATE FORM', { 'Page Title': 'ATTENDEE PROFILE' });
                                    history.push(`/ticket/O${organization.pk}/E${event.pk}/settings/forms`);
                                }}
                            />
                        )}
                        {Boolean(formEntries.length) && edit && (
                            <>
                                <FormPreview fields={formEntriesEditable} form={form} onChangeForm={setForm} locale={locale} usage={usage} />
                                <Button.Group className={Style['button-group-edit']}>
                                    <Button primary isLoading={isSubmitting} disabled={isSubmitting} onClick={handleSubmit} label={t('ticket.common.action.save')} />
                                    <Button
                                        onClick={() => {
                                            setEdit(false);
                                            setFormObject({ ...initialForm });
                                            track('ACTION > CANCEL', { 'Page Title': usage === 'attendee' ? 'ATTENDEE PROFILE' : 'ORDER DETAILS' });
                                        }}
                                        label={t('ticket.common.action.cancel')}
                                    />
                                </Button.Group>
                            </>
                        )}
                        {Boolean(formEntries.length) && !edit && <FormAnswers fields={formEntries} answers={answers} locale={locale} />}
                    </Card.Content>
                )}
            </Card>
        </>
    );
}

export default function FilledForm({ className = '', attendee = null, buyerForm = false, onEdit = () => {}, ...rest }) {
    const [loading, setLoading] = React.useState(true);
    const { t } = useTranslation();

    const isDisplayable = form_field => {
        if (!form_field) {
            return false;
        }

        if (form_field.field_category === 'custom') {
            return !customfieldTypesNotDisplayable.includes(form_field.field_type);
        }
        return !fieldsNotDisplayable.includes(form_field.weez_id) && form_field.displayable;
    };

    //Format answers
    const answers = React.useMemo(() => {
        if (attendee) {
            if (!attendee?.form || !attendee.form.length) {
                return {};
            }

            return attendee.form.reduce((acc, answer) => {
                acc[answer.weez_id] = {
                    answer: answer.answer,
                    question: answer.question,
                    field: answer.field
                };
                return acc;
            }, {});
        }

        if (buyerForm) {
            if (!buyerForm.length) {
                return {};
            }
            return buyerForm.reduce((acc, answer) => {
                acc[answer.weez_id] = {
                    answer: answer.answer,
                    question: answer.question,
                    field: answer.field
                };
                return acc;
            }, {});
        }

        return {};
    }, [attendee]);

    //FormEntries list
    const formEntries = React.useMemo(() => {
        let _formEntries = [];

        //Generates the buyer and attendee form on the order page
        Object.keys(answers).forEach(key => {
            if (isDisplayable(answers[key].field) && !_formEntries.some(fe => fe.form_field.weez_id === key)) {
                let new_fe = new FormEntry();
                new_fe.usage = buyerForm ? 'buyer' : 'attendee';
                new_fe.required = false;
                new_fe.form_field = FieldModel.fromJSON(answers[key].field);
                _formEntries.push(new_fe);
            }
            if (!answers[key].field && systemFieldsNotEditable.includes(key)) {
                let new_field = new FieldModel({
                    weez_id: key,
                    label: t(`ticket.manager.attendees.detail.filledForm.${key}`),
                    description: null,
                    default_value: null,
                    field_type: 'textfield',
                    subtype: null,
                    question_choices: [],
                    is_standard: false,
                    field_category: 'custom',
                    order: Infinity,
                    displayable: true
                });

                let new_fe = new FormEntry();
                new_fe.usage = buyerForm ? 'buyer' : 'attendee';
                new_fe.required = true;
                new_fe.form_field = new_field;
                _formEntries.push(new_fe);
            }
        });

        return _formEntries.sort(function(a, b) {
            return a['form_field']['order'] - b['form_field']['order'];
        });
    }, [attendee, buyerForm, fieldsNotDisplayable, answers]);

    //Initialize hiddenForm with answers of hidden fields (same format 'initialForm', for can merge before submit)
    const hiddenForm = React.useMemo(() => {
        return Object.keys(answers).reduce((acc, key) => {
            if (!isDisplayable(answers[key].field) && !systemFieldsNotEditable.includes(key)) {
                acc[key] = answers[key].answer;
            }
            return acc;
        }, {});
    }, [answers, isDisplayable]);

    //Initialize form with formEntries and answers
    const initialForm = React.useMemo(() => {
        let fes = formEntries.reduce((acc, field) => {
            //Flag the field "divider", to be able to delete it before submitting the form
            if (field.form_field.field_type === 'divider') {
                acc[field.form_field.weez_id] = 'form_field_divider';
                return acc;
            }

            //Allows not to add fields to the form that are only used for internal operation or are simply not editable
            if (systemFieldsNotEditable.includes(field.form_field.weez_id)) {
                return acc;
            }

            acc[field.form_field.weez_id] = answers[field.form_field?.weez_id] ? answers[field.form_field.weez_id].answer : null;
            return acc;
        }, {});

        setLoading(false);

        return fes;
    }, [formEntries, answers]);

    if (!attendee && !buyerForm) {
        return null;
    }

    return (
        <FilledFormCard
            attendee={attendee}
            answers={answers}
            formEntries={formEntries}
            initialForm={initialForm}
            hiddenForm={hiddenForm}
            loading={loading}
            onEdit={onEdit}
            {...rest}
        />
    );
}

FilledForm.propTypes = {
    title: PropTypes.string,
    className: PropTypes.string,
    buyerForm: PropTypes.bool
};
